import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Radio, Space, Tag, Typography, Statistic, Row, Col, Button, Checkbox, message, Grid, Empty } from 'antd';
import { BarChartOutlined, ReloadOutlined, GiftOutlined } from '@ant-design/icons';
import { getItemIcon } from './constants';

const { Text } = Typography;

const API_BASE_URL = '';
const { useBreakpoint } = Grid;

const DropStats = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState(null);      // { HT: {...}, PB: {...} }
  const [bossType, setBossType] = useState('HT');
  const [loading, setLoading] = useState(false);
  const [onlyImportant, setOnlyImportant] = useState(true);  // 只看有图标的重要掉落物（默认勾选）

  // 加载掉落统计
  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/stats/drop-rates`);
      if (res.ok) {
        setData(await res.json());
      } else {
        message.error('加载掉落统计失败');
      }
    } catch (e) {
      console.error(e);
      message.error('加载掉落统计失败');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const bucket = data?.[bossType] || { totalRuns: 0, totalRounds: 0, items: [] };
  // 重要掉落物过滤：只保留有图标（ITEM_ICONS 里配置了图片）的物品
  const displayItems = onlyImportant ? bucket.items.filter(it => getItemIcon(it.name)) : bucket.items;

  // 表格列：R1/R2 不分列，按「总轮数」展示每个物品的出现轮数与掉率
  const columns = [
    {
      title: '物品',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
      render: (name) => {
        const icon = getItemIcon(name);
        return (
          <Space size={6}>
            {icon && <img src={icon} alt={name} style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle' }} />}
            <Text strong>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: '出现轮数',
      dataIndex: 'roundCount',
      key: 'roundCount',
      width: 130,
      render: (v) => `${v} / ${bucket.totalRounds}`,
    },
    {
      title: '掉落概率',
      dataIndex: 'rate',
      key: 'rate',
      width: 110,
      render: (v) => <Tag color="purple">{(v * 100).toFixed(1)}%</Tag>,
    },
    {
      title: '累计掉落数量',
      dataIndex: 'totalQty',
      key: 'totalQty',
      width: 130,
      render: (v) => <Text>{v ?? 0}</Text>,
    },
  ];

  return (
    <div>
      <Card
        title={<><BarChartOutlined /> 掉落统计 <Tag color="blue">HT / PB</Tag></>}
        extra={isMobile
          ? <Button icon={<ReloadOutlined />} onClick={loadStats} loading={loading} />
          : <Space><Text type="secondary">出现过的轮数 / 总轮数（R1、R2 各算一轮）</Text><Button icon={<ReloadOutlined />} onClick={loadStats} loading={loading}>刷新</Button></Space>}
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Text strong>Boss 类型：</Text>
            <Radio.Group
              value={bossType}
              onChange={(e) => setBossType(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size={isMobile ? 'small' : 'middle'}
            >
              <Radio.Button value="HT">HT (Horntail)</Radio.Button>
              <Radio.Button value="PB">PB (Pink Bean)</Radio.Button>
            </Radio.Group>
          </Space>

          <Row gutter={[16, 16]}>
            <Col xs={8} md={6}><Statistic title="总 Run 数" value={bucket.totalRuns} /></Col>
            <Col xs={8} md={6}><Statistic title="总轮数" value={bucket.totalRounds} /></Col>
            <Col xs={8} md={6}><Statistic title="物品种类数" value={bucket.items.length} prefix={<GiftOutlined />} /></Col>
          </Row>
        </Space>
      </Card>

      <Card
        title={<><BarChartOutlined /> 掉落概率明细</>}
        extra={!isMobile && <Checkbox checked={onlyImportant} onChange={(e) => setOnlyImportant(e.target.checked)}>只看重要掉落物（有图标）</Checkbox>}
        style={{ marginBottom: 16 }}
      >
        {isMobile && (
          <Checkbox checked={onlyImportant} onChange={(e) => setOnlyImportant(e.target.checked)} style={{ marginBottom: 12 }}>
            只看重要掉落物（有图标）
          </Checkbox>
        )}
        {isMobile ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {displayItems.map(item => {
              const icon = getItemIcon(item.name);
              return (
                <Card size="small" key={item.name} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    {icon && <img src={icon} alt={item.name} style={{ width: 24, height: 24, objectFit: 'contain' }} />}
                    <Text strong style={{ fontSize: 15 }}>{item.name}</Text>
                  </div>
                  <Row gutter={8}>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>出现轮数</Text>
                      <Text strong>{item.roundCount} / {bucket.totalRounds}</Text>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>掉率</Text>
                      <Tag color="purple" style={{ marginTop: 2 }}>{(item.rate * 100).toFixed(1)}%</Tag>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>累计数量</Text>
                      <Text strong>{item.totalQty ?? 0}</Text>
                    </Col>
                  </Row>
                </Card>
              );
            })}
            {displayItems.length === 0 && <Empty description="暂无掉落数据" />}
          </Space>
        ) : (
          <Table
            dataSource={displayItems}
            columns={columns}
            rowKey="name"
            size="small"
            loading={loading}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: (total) => `共 ${total} 个物品` }}
            locale={{ emptyText: '暂无掉落数据' }}
          />
        )}
      </Card>
    </div>
  );
};

export default DropStats;
